/**
 * Simple SupplyPoint Veri Yapısı
 * Karmaşık interconnected arrays yerine sadece feeders ve components
 */

// Component türleri
export const COMPONENT_KINDS = {
  SUPPLY: "supply",           // Şebeke bağlantısı
  METER_PANEL: "meter_panel", // Sayaç panosu (mf-03-a)
  DIST_PANEL: "dist_panel",   // Dağıtım panosu (mf-03-b)
  PANEL: "panel",             // Elektrik paneli
  BRANCH: "branch",           // Branch circuit
  PANEL_OUTPUT: "panel_output", // Panel çıkışı
  SOCKET_LINE: "socket_line", // Priz hattı (birden fazla priz içeren)
  MACHINE_LINE: "machine_line", // Makine hattı (tek makine)
  SWICH_LINE:"swich_line",
  LOAD: "load"                // Son yük
};

// Feeder türleri
export const FEEDER_KINDS = {
  MAIN_FEEDER: "main_feeder",           // Ana besleyici (supply → meter_panel)
  DIST_FEEDER: "dist_feeder",           // Dağıtım besleyici (meter_panel → dist_panel)
  PANEL_FEEDER: "panel_feeder",         // Panel besleyici (meter_panel → dist_panel)
  SUB_PANEL_FEEDER: "sub_panel_feeder", // Alt panel besleyici (dist_panel → panel)
  BRANCH_CIRCUIT: "branch_circuit",     // Branch devresi (panel → load)
  SOCKET_CIRCUIT: "socket_circuit",     // Priz devresi
  LIGHTING_CIRCUIT: "lighting_circuit", // Aydınlatma devresi
  MACHINE_CIRCUIT: "machine_circuit",   // Makine devresi
  LOWCURRENT_CIRCUIT: "lowcurrent_circuit", // Zayıf akım devresi (zil, telefon vb.)
  SUB_FEEDER: "sub_feeder"              // Alt besleyici (deprecated, backward compatibility)
};

// Feeder tipleri - basitleştirilmiş
export const FEEDER_TYPES = {
  MAIN: "main",         // Ana besleyici (supply → panel)
  SUB: "sub",          // Alt besleyici (panel → panel)
  BRANCH: "branch",    // Branch circuit (panel → load)
  LOAD: "load"         // Direct load connection
};

/**
 * Component yapısı
 */
export class SupplyComponent {
  constructor({
    id,
    kind,
    name,
    electrical = {},
    incomingFeeders = [],
    outgoingFeeders = [],
    entries = [],
    outs = [],
    position = null,
    rotation = null,
    metadata = {},
    parentComponentId = null // 🆕 Parent component ID (varsa)
  }) {
    this.id = id;
    this.kind = kind; // COMPONENT_KINDS'den biri
    this.name = name;
    this.electrical = {
      phaseCount: electrical.phaseCount || 3,
      installedPower: electrical.installedPower || 0,
      demandPower: electrical.demandPower || 0,
      current: electrical.current || 0,
      demandFactor: electrical.demandFactor || "60-40%",
      purposeOfUse: electrical.purposeOfUse || "Mesken",
      ...electrical
    };
    this.incomingFeeders = incomingFeeders; // Bu componente gelen feeder ID'leri
    this.outgoingFeeders = outgoingFeeders; // Bu componentten çıkan feeder ID'leri
    this.entries = entries; // Sigorta, kaçak akım rölesi vb giriş elemanları [{type: 'breaker', MCB_No: 433, ...}]
    this.outs = outs; // Çıkış elemanları [{type: 'breaker', MCB_No: 434, lineType: 'socket', ...}]
    this.position = position; // 3D position {x, y, z}
    this.rotation = rotation; // 3D rotation {x, y, z}
    this.metadata = metadata; // Housing type vb ek bilgiler
    this.parentComponentId = parentComponentId; // 🆕 Parent component ID (varsa)
  }
}

/**
 * Feeder yapısı
 */
export class SupplyFeeder {
  constructor({
    id,
    from,
    to,
    type,
    kind,
    power = {},
    phaseCount = 3,
    cable = {},
    protection = {},
    metadata = {}
  }) {
    this.id = id;
    this.from = from; // Kaynak component ID
    this.to = to;     // Hedef component ID
    this.type = type; // FEEDER_TYPES'dan biri (mf-03-a, mf-03-b, vb)
    this.kind = kind; // FEEDER_KINDS'dan biri (main_feeder, sub_feeder, vb)
    this.phaseCount = phaseCount;

    this.power = {
      installedPower: power.installedPower || 0,
      demandPower: power.demandPower || 0,
      current: power.current || 0,
      ...power
    };

    this.cable = {
      type: cable.type || "NYY",
      material: cable.material || "Cu",
      section: cable.section || 2.5,
      length: cable.length || 0,
      currentCap: cable.currentCap || 0,
      count: cable.count || 1,
      housing: cable.housing || "pipe",
      ...cable
    };

    this.protection = {
      breaker1: protection.breaker1 || null, // MCB
      breaker2: protection.breaker2 || null, // RCD
      ...protection
    };

    this.metadata = metadata; // Connection type, masterNodeId vb ek bilgiler
  }
}

/**
 * Simple SupplyPoint ana yapısı
 */
export class SimpleSupplyPoint {
  constructor({
    feeders = [],
    components = [],
    metadata = {}
  }) {
    this.feeders = feeders.map(f => f instanceof SupplyFeeder ? f : new SupplyFeeder(f));
    this.components = components.map(c => c instanceof SupplyComponent ? c : new SupplyComponent(c));
    this.metadata = {
      version: "1.0",
      createdAt: new Date().toISOString(),
      ...metadata
    };
  }

  /**
   * Component bulma helper'ları
   */
  getComponent(id) {
    return this.components.find(c => c.id === id);
  }

  getFeeder(id) {
    return this.feeders.find(f => f.id === id);
  }

  getComponentByKind(kind) {
    return this.components.filter(c => c.kind === kind);
  }

  getFeedersByType(type) {
    return this.feeders.filter(f => f.type === type);
  }

  /**
   * Relationship helper'ları
   */
  getOutgoingFeeders(componentId) {
    return this.feeders.filter(f => f.from === componentId);
  }

  getIncomingFeeders(componentId) {
    return this.feeders.filter(f => f.to === componentId);
  }

  getUpstreamComponents(startComponentId) {
    const chain = [];
    let currentId = startComponentId;
    const visited = new Set();

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const component = this.getComponent(currentId);
      if (!component) break;

      chain.push(component);

      // Bu componenti besleyen feeder'ı bul
      const incomingFeeder = this.getIncomingFeeders(currentId)[0];
      if (incomingFeeder) {
        currentId = incomingFeeder.from;
      } else {
        break;
      }
    }

    return chain;
  }

  /**
   * Validation
   */
  validate() {
    const errors = [];

    // Her feeder'ın from/to component'leri var mı?
    this.feeders.forEach(feeder => {
      if (!this.getComponent(feeder.from)) {
        errors.push(`Feeder ${feeder.id}: from component ${feeder.from} not found`);
      }
      if (!this.getComponent(feeder.to)) {
        errors.push(`Feeder ${feeder.id}: to component ${feeder.to} not found`);
      }
    });

    // Component'lerin feeder referansları doğru mu?
    this.components.forEach(component => {
      component.outgoingFeeders.forEach(feederId => {
        const feeder = this.getFeeder(feederId);
        if (!feeder || feeder.from !== component.id) {
          errors.push(`Component ${component.id}: invalid outgoing feeder ${feederId}`);
        }
      });

      component.incomingFeeders.forEach(feederId => {
        const feeder = this.getFeeder(feederId);
        if (!feeder || feeder.to !== component.id) {
          errors.push(`Component ${component.id}: invalid incoming feeder ${feederId}`);
        }
      });
    });

    return errors;
  }
}

/**
 * Test data factory
 */
export function createTestSimpleSupplyPoint() {
  const supply = new SupplyComponent({
    id: "component_supply",
    kind: COMPONENT_KINDS.SUPPLY,
    name: "ŞEBEKE",
    electrical: {
      phaseCount: 3,
      installedPower: 0,
      demandPower: 0,
      current: 0
    },
    outgoingFeeders: ["feeder_main_001"]
  });

  const meterPanel = new SupplyComponent({
    id: "component_meter_panel",
    kind: COMPONENT_KINDS.METER_PANEL,
    name: "Sayaç Panosu",
    electrical: {
      phaseCount: 3,
      installedPower: 1500,
      demandPower: 1200,
      current: 5.5
    },
    incomingFeeders: ["feeder_main_001"],
    outgoingFeeders: ["feeder_sub_001"]
  });

  const distPanel = new SupplyComponent({
    id: "component_dist_panel",
    kind: COMPONENT_KINDS.DIST_PANEL,
    name: "ZKT",
    electrical: {
      phaseCount: 3,
      installedPower: 1500,
      demandPower: 1200,
      current: 5.5
    },
    incomingFeeders: ["feeder_sub_001"],
    outgoingFeeders: []
  });

  const mainFeeder = new SupplyFeeder({
    id: "feeder_main_001",
    from: "component_supply",
    to: "component_meter_panel",
    type: FEEDER_TYPES.MF_03_A,
    kind: FEEDER_KINDS.MAIN_FEEDER,
    power: {
      installedPower: 1500,
      demandPower: 1200,
      current: 5.5
    },
    cable: {
      type: "NYY",
      material: "Cu",
      section: 10,
      length: 25,
      currentCap: 44
    },
    protection: {
      breaker1: { type: "MCB", current: 16, curve: "C" },
      breaker2: { type: "RCD", current: 16, sensitivity: 300 }
    }
  });

  const subFeeder = new SupplyFeeder({
    id: "feeder_sub_001",
    from: "component_meter_panel",
    to: "component_dist_panel",
    type: FEEDER_TYPES.MF_03_B,
    kind: FEEDER_KINDS.SUB_FEEDER,
    power: {
      installedPower: 1500,
      demandPower: 1200,
      current: 5.5
    },
    cable: {
      type: "NYY",
      material: "Cu",
      section: 10,
      length: 15,
      currentCap: 44
    },
    protection: {
      breaker1: { type: "MCB", current: 16, curve: "C" },
      breaker2: { type: "RCD", current: 16, sensitivity: 300 }
    }
  });

  return new SimpleSupplyPoint({
    feeders: [mainFeeder, subFeeder],
    components: [supply, meterPanel, distPanel],
    metadata: {
      testData: true,
      description: "Basit 3 component'li test verisi"
    }
  });
}

// Alias exports for gradual rename: supplyPoint
// Export the class under the new name as well so other modules can import { SupplyPoint }
export { SimpleSupplyPoint as SupplyPoint };

// Alias for test factory
export const createTestSupplyPoint = createTestSimpleSupplyPoint;